/**
 * Hybrid runtime/build-time config wrapper.
 *
 * Resolution order for every key:
 *   1. window.APP_CONFIG[KEY]   -> runtime (ECR / EC2 startup envsubst)
 *   2. import.meta.env[KEY]     -> Vite build-time (.env or CI variables)
 *
 * Behavior in each environment:
 *   - Local dev:        public/runtime-config.js is a no-op, runtime is empty,
 *                       wrapper falls back to import.meta.env from .env
 *   - Legacy build:     build/runtime-config.js is the no-op default shipped
 *                       from public/, runtime is empty, wrapper falls back to
 *                       the values baked in at build time
 *   - New ECR deploy:   container entrypoint runs `envsubst` over
 *                       runtime-config.js.template, populating window.APP_CONFIG
 *                       with the runtime values, which then win
 */

const runtime =
  (typeof window !== "undefined" && window && window.APP_CONFIG) || {};
const buildTime = import.meta.env || {};

const isUsable = (value) =>
  value !== undefined && value !== null && value !== "";

/**
 * Resolve a single VITE_* key, preferring runtime config over build-time env.
 * Useful for keys that don't have a named getter on `appConfig`.
 *
 * @param {string} key - The full key, e.g. "VITE_API_HOST".
 * @returns {string | undefined}
 */
export const getConfig = (key) => {
  const fromRuntime = runtime[key];
  if (isUsable(fromRuntime)) return fromRuntime;
  return buildTime[key];
};

/**
 * Same as `getConfig` but with an explicit fallback when neither runtime nor
 * build-time provides a value.
 *
 * @param {string} key
 * @param {string} fallback
 * @returns {string}
 */
export const getConfigOr = (key, fallback) => {
  const value = getConfig(key);
  return isUsable(value) ? value : fallback;
};

export const appConfig = {
  apiHost: getConfig("VITE_API_HOST"),
  apiBaseUrl: getConfig("VITE_API_BASE_URL"),
  socketHost: getConfig("VITE_SOCKET_HOST"),
  communicationHost: getConfig("VITE_COMMUNICATION_HOST"),
  streamingSocketHost: getConfig("VITE_STREAMING_SOCKET_HOST"),
  streamingHost: getConfig("VITE_STREAMING_HOST"),
  paymentHost: getConfig("VITE_PAYMENT_HOST"),
  contentsHost: getConfig("VITE_CONTENTS_HOST"),
  f2fHost: getConfig("VITE_F2F_HOST"),
  f2fSocketHost: getConfig("VITE_F2F_SOCKET_HOST"),
  adsHost: getConfig("VITE_ADS_HOST"),
  utilitiesHost: getConfig("VITE_UTILITIES_HOST"),
  googleClientId: getConfig("VITE_GOOGLE_CLIENT_ID"),
  googleAnalyticsKey: getConfig("VITE_GOOGLE_ANALYTICS_KEY"),
  recaptchaSiteKey: getConfig("VITE_ReCAPTCHA_SITE_KEY"),
  zendeskKey: getConfig("VITE_ZENDESK_KEY"),
  environment: getConfig("VITE_ENVIRONMENT"),
  domain: getConfig("VITE_DOMAIN"),
  performerProfileTestUser: getConfig("VITE_PERFORMER_PROFILE_TEST_USER"),
};

export default appConfig;
